import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import moment, { MomentInput } from 'moment';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FileUpload from '../../../../shared/components/FileUpload';
import { IOSSwitch } from '../../../../shared/components/IOSSwitch';
import { IFormField } from '../../../../shared/interfaces/forms/IForms';
import { IUser } from '../../../../shared/interfaces/user/IUser';
import { lightPalette } from '../../../../shared/mui/palettes';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import i18n from '../../../../utils/localization/i18n';
interface IProps {
  userKey: IFormField;
  control: Control<IUser>;
}
const FormField = ({ userKey, control }: IProps) => {
  const { t } = useTranslation();
  return (
    <Grid size={userKey.colSize}>
      <Controller
        name={userKey.key as keyof IUser}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          const errorMessage = error?.message
            ? t(error?.message, {
                minLength: userKey?.minLength,
                maxLength: userKey?.maxLength,
              })
            : false;
          return userKey.inputType === 'file' ? (
            <FileUpload
              file={value as File | FileList | null}
              label={t(userKey.label)}
              onChange={onChange}
              multiple={userKey.multiple}
            />
          ) : userKey.inputType === 'checkbox' ? (
            <>
              <Typography
                variant={'subtitle2'}
                color={lightPalette.text.gray}
                sx={{
                  fontWeight: '300',
                }}
              >
                {t(userKey.label)}
              </Typography>
              <IOSSwitch
                checked={Boolean(value)}
                onChange={(e) => onChange(e.target.checked)}
              />
            </>
          ) : userKey.inputType === 'date' ? (
            <FormControl fullWidth sx={{ width: '100%' }}>
              <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={i18n.language}
              >
                <DemoContainer components={['DatePicker']}>
                  <DatePicker
                    sx={{ width: '100%' }}
                    format="DD.MM.YYYY"
                    label={t(userKey.label)}
                    value={moment(value as MomentInput)}
                    onChange={onChange}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </FormControl>
          ) : userKey.inputType === 'select' ? (
            <FormControl fullWidth>
              <InputLabel
                className={!!error?.message?.length ? 'Mui-error' : ''}
                id={`${userKey.key}-label`}
              >
                {t(userKey.label)}
              </InputLabel>
              <Select
                error={!!error?.message}
                labelId={`${userKey.key}-label`}
                id={`${userKey.key}-select`}
                value={value ?? (userKey?.multiple ? [] : '')}
                label={t(userKey.label)}
                onChange={onChange}
                multiple={userKey?.multiple}
              >
                {userKey?.values?.map((value) => {
                  return (
                    <MenuItem key={value.key} value={value.key}>
                      {t(value.label)}
                    </MenuItem>
                  );
                })}
              </Select>
              {!!error?.message?.length && (
                <FormHelperText>{errorMessage}</FormHelperText>
              )}
            </FormControl>
          ) : userKey.inputType === 'textarea' ? (
            <TextField
              error={!!error?.message}
              helperText={errorMessage}
              value={value ?? ''}
              onChange={onChange}
              label={t(userKey.label)}
              sx={{ width: '100%' }}
              multiline
              rows={4}
            />
          ) : (
            <TextField
              error={!!error?.message}
              helperText={errorMessage}
              value={value ?? ''}
              onChange={onChange}
              label={t(userKey.label)}
              sx={{ width: '100%' }}
              type={userKey.inputType}
            />
          );
        }}
      />
    </Grid>
  );
};

export default FormField;
